import './padMapping.css';
import {
  PAD_ACTIONS,
  addPadButton,
  padBindings,
  padButtonLabel,
  removePadButton,
  resetPadBindings,
  type PadAction,
} from '../core/gamepad-config';

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const button = (className: string, text: string): HTMLButtonElement => {
  const node = el('button', className, text);
  node.type = 'button';
  return node;
};

/**
 * Gamepad-gombok hozzárendelése a beállításokhoz. Egy funkcióhoz több gomb
 * is tartozhat; ha egy gombot máshová teszel, áthelyeződik.
 */
export function appendPadMapping(container: HTMLElement): void {
  const section = el('section', 'csf-padmap');
  section.setAttribute('aria-label', 'Gamepad gombok beállítása');

  const head = el('div', 'csf-padmap__head');
  const headText = el('div', 'csf-padmap__head-text');
  headText.append(
    el('div', 'csf-padmap__title', 'GAMEPAD GOMBOK'),
    el('div', 'csf-padmap__sub', 'Egy funkcióhoz több gomb is rendelhető.'),
  );
  const toggle = button('csf-padmap__toggle', 'MEGNYITÁS');
  toggle.setAttribute('aria-expanded', 'false');
  head.append(headText, toggle);

  const body = el('div', 'csf-padmap__body');
  body.hidden = true;

  const intro = el(
    'p',
    'csf-padmap__intro',
    'Kattints a „GOMB +” gombra, majd nyomd le a gamepadon a hozzárendelni kívánt gombot. Egy gomb csak egy funkcióhoz tartozhat; ha máshová teszed, áthelyeződik. A bal kar és a függőleges analóg tengely továbbra is kormány/gáz-fék marad.',
  );

  const rows = el('div', 'csf-padmap__rows');
  const status = el('div', 'csf-padmap__status', '');
  const actions = el('div', 'csf-padmap__actions');
  const reset = button('csf-padmap__reset', 'ALAPÉRTELMEZÉS');
  actions.append(reset);

  let capture: PadAction | null = null;
  let prev: boolean[] = [];
  let raf = 0;
  let hasPad = false;

  const padNow = (): Gamepad | null => {
    const pads = navigator.getGamepads?.() ?? [];
    return Array.from(pads).find((pad): pad is Gamepad => pad !== null) ?? null;
  };

  const stopCapture = (): void => {
    capture = null;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    prev = [];
    renderRows();
  };

  const startCapture = (action: PadAction): void => {
    const pad = padNow();
    hasPad = pad !== null;
    if (!pad) {
      status.textContent = 'Nincs gamepad csatlakoztatva. Csatlakoztasd, majd próbáld újra.';
      status.classList.add('is-warn');
      return;
    }
    capture = action;
    prev = pad.buttons.map((b) => b.pressed);
    status.classList.remove('is-warn');
    status.textContent = 'Nyomj egy gombot a gamepadon… (a MÉGSE gombbal kiléphetsz)';
    renderRows();
    const tick = (): void => {
      if (!section.isConnected || !capture) {
        stopCapture();
        return;
      }
      const current = padNow();
      if (current) {
        hasPad = true;
        const pressed = current.buttons.map((b) => b.pressed);
        for (let i = 0; i < pressed.length; i++) {
          if (pressed[i] && !prev[i]) {
            const action = capture;
            if (!action) return;
            addPadButton(action, i);
            capture = null;
            if (raf) cancelAnimationFrame(raf);
            raf = 0;
            prev = [];
            status.textContent = `Hozzárendelve: ${padButtonLabel(i)} → ${PAD_ACTIONS.find((a) => a.id === action)?.label ?? ''}`;
            renderRows();
            return;
          }
        }
        prev = pressed;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  };

  const renderRows = (): void => {
    rows.replaceChildren();
    for (const def of PAD_ACTIONS) {
      const row = el('div', 'csf-padmap__row');
      row.dataset.action = def.id;
      const label = el('span', 'csf-padmap__label', def.label);
      const chips = el('span', 'csf-padmap__chips');
      const list = padBindings[def.id];
      if (list.length === 0) chips.append(el('span', 'csf-padmap__empty', 'nincs gomb'));
      for (const index of list) {
        const chip = button('csf-padmap__chip', `${padButtonLabel(index)} ×`);
        chip.title = 'Eltávolítás';
        chip.setAttribute('aria-label', `${def.label}: ${padButtonLabel(index)} eltávolítása`);
        chip.addEventListener('click', () => {
          removePadButton(def.id, index);
          renderRows();
        });
        chips.append(chip);
      }
      const add = button(
        `csf-padmap__add${capture === def.id ? ' is-capturing' : ''}`,
        capture === def.id ? 'FIGYELEK…' : 'GOMB +',
      );
      add.disabled = capture !== null;
      add.setAttribute('aria-label', `${def.label}: gomb hozzáadása`);
      add.addEventListener('click', () => startCapture(def.id));
      row.append(label, chips, add);
      rows.append(row);
    }
    if (capture) {
      status.hidden = false;
      const cancel = button('csf-padmap__cancel', 'MÉGSE');
      cancel.addEventListener('click', () => {
        status.textContent = 'Hozzárendelés megszakítva.';
        stopCapture();
      });
      if (!status.querySelector('.csf-padmap__cancel')) status.append(cancel);
    } else {
      status.querySelector('.csf-padmap__cancel')?.remove();
      if (!hasPad) {
        // ne írjuk felül a hozzárendelés visszajelzését
      }
    }
    toggle.textContent = body.hidden ? 'MEGNYITÁS' : 'BEZÁRÁS';
  };

  toggle.addEventListener('click', () => {
    body.hidden = !body.hidden;
    toggle.setAttribute('aria-expanded', String(!body.hidden));
    toggle.textContent = body.hidden ? 'MEGNYITÁS' : 'BEZÁRÁS';
    if (body.hidden) stopCapture();
    else renderRows();
  });

  reset.addEventListener('click', () => {
    resetPadBindings();
    stopCapture();
    status.classList.remove('is-warn');
    status.textContent = 'Alapértelmezett gamepad-kiosztás visszaállítva.';
    renderRows();
  });

  body.append(intro, rows, status, actions);
  section.append(head, body);
  container.append(section);
  renderRows();
}
